import { Map, OrderedMap } from 'immutable';

import mappers from 'mappers';
import { EMPTY_MAP } from 'app/app.constants';
import { WELLS_ACTIONS as ACTIONS } from 'features/wells/wells.constants';
import {
  compareDates,
  compareNumbers,
  wellBoreDataMapper,
} from 'features/wells/schematics/helpers/utils';

const initialState = Map({
  pageParams: Map({
    currentPage: 1,
    pageSize: 50,
  }),
  pageResults: EMPTY_MAP,
  wellboreIntData: {
    data: null,
    isFetching: true,
    error: null,
  },
  elementsData: [],
  serviceProviders: [],
  corrosionMeasureTools: [],
  newServiceProvider: {},
  newCorrosionMeasureTool: {},
  corrosionLogsList: [],
});

const wellboreReducer = (state = initialState, action) => {
  const { type, error, payload } = action;

  switch (type) {
    case ACTIONS.RECEIVE_WELLBORE: {
      if (error) return state;

      return state.setIn(
        ['pageResults', payload.wellboreId.toString()],
        mappers.Wellbore.from(payload),
      );
    }

    case ACTIONS.RECEIVE_WELLBORES_PAGE: {
      if (error) return state;

      const { results: wellbores, ...pageParams } = payload;

      return state
        .set('pageParams', mappers.PageParams.from(pageParams))
        .set(
          'pageResults',
          OrderedMap(
            wellbores.map((wellbore) => [
              wellbore.wellboreId.toString(),
              mappers.Wellbore.from(wellbore),
            ]),
          ),
        );
    }

    case ACTIONS.UPDATE_WELLBORE: {
      if (error) return state;

      const { wellboreId } = action;

      return state.setIn(['pageResults', wellboreId.toString()], payload);
    }

    case ACTIONS.CLEAR_WELLBORES: {
      return initialState;
    }

    case ACTIONS.WELLBORE_INT_FETCH_STARTED: {
      return state.set('wellboreIntData', {
        data: null,
        isFetching: true,
        error: null,
      });
    }

    case ACTIONS.WELLBORE_INT_FETCH_SUCCEEDED: {
      const schematicVersions = payload ?? [];
      const schematicVersionsSorted = schematicVersions
        .sort(compareDates)
        .reverse();
      const versionsAndComponentsSorted = schematicVersionsSorted.map(
        (version) => ({
          ...version,
          components: wellBoreDataMapper(
            version.components.sort(compareNumbers),
          ),
        }),
      );

      return state.set('wellboreIntData', {
        data: versionsAndComponentsSorted,
        isFetching: false,
        error: null,
      });
    }

    case ACTIONS.WELLBORE_INT_FETCH_FAILED: {
      return state.set('wellboreIntData', {
        data: null,
        isFetching: false,
        error: payload,
      });
    }

    case ACTIONS.SCHEMATIC_CREATE_SUCCEEDED: {
      const schematicData = state.get('wellboreIntData').data;
      const previousComponentsData =
        schematicData !== null && schematicData.length > 0
          ? schematicData[0].components
          : [];
      const unsortedComponentsData =
        previousComponentsData !== null
          ? [...previousComponentsData, payload]
          : [payload];
      const sortedComponentsData = unsortedComponentsData.sort(compareNumbers);
      if (schematicData.length === 0) {
        return state.set('wellboreIntData', {
          data: [{ components: sortedComponentsData }],
          isFetching: false,
          error: null,
        });
      }
      const newSchematicData = schematicData.map((version, index) => {
        if (schematicData.length === 1 || 0 === index) {
          return { ...version, components: sortedComponentsData };
        }
        return version;
      });

      return state.set('wellboreIntData', {
        data: newSchematicData,
        isFetching: false,
        error: null,
      });
    }

    case ACTIONS.SCHEMATIC_COMPONENT_DELETE_STARTED: {
      const schematicData = state.get('wellboreIntData').data;
      const previousComponentsData =
        schematicData !== null && schematicData.length > 0
          ? schematicData[0].components
          : [];
      const newComponentsData =
        previousComponentsData.length !== 0
          ? previousComponentsData.filter(
              (_component, index) => index !== payload,
            )
          : previousComponentsData;

      const newSchematicData = schematicData.map((version, index) => {
        if (0 === index) {
          return { ...version, components: newComponentsData };
        }
        return version;
      });

      return state.set('wellboreIntData', {
        data: newSchematicData,
        isFetching: false,
        error: null,
      });
    }

    case ACTIONS.SCHEMATIC_COMPONENT_UPDATE_STARTED: {
      const { componentData, componentIndex } = payload;
      const schematicData = state.get('wellboreIntData').data;
      const previousComponentsData =
        schematicData !== null && schematicData.length > 0
          ? schematicData[0].components
          : [];
      const newComponentsData =
        previousComponentsData.length !== 0
          ? previousComponentsData.map((component, index) => {
              if (index !== componentIndex) {
                return component;
              }
              return componentData;
            })
          : previousComponentsData;
      const newSchematicData = schematicData.map((version, index) => {
        if (0 === index) {
          return { ...version, components: newComponentsData };
        }
        return version;
      });

      return state.set('wellboreIntData', {
        data: newSchematicData,
        isFetching: false,
        error: null,
      });
    }

    case ACTIONS.LOAD_ALL_ELEMENTS_LIST:
      if (error) return state;

      return state.set('elementsData', payload);

    default:
      return state;
  }
};

export default wellboreReducer;
